import type { ComponentProps } from 'react';
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { ArticleImageType } from 'graphql-ts-types';
import type {
  BackgroundColorProps,
  BoxShadowProps,
  DisplayProps,
  LeftProps,
  OverflowProps,
  PositionProps,
  TopProps,
  ZIndexProps,
} from 'styled-system';
import {
  backgroundColor,
  boxShadow,
  display,
  left,
  overflow,
  position,
  space,
  top,
  zIndex,
} from 'styled-system';

import ImageCarousel from 'common/components/image-carousel/ImageCarousel';
import type { SpaceProps } from 'common/theme/system-types';
import type { GroupValueArticle } from '../../../product-mvc-buttons/components/types';
import { GO_TO_TOP_Z_INDEX } from '../../constants/z-index';
import type { ThemeBottomProps, TransformProps } from '../../theme/system-utilities';
import { themeBottom, themeHeight, themeWidth, transform } from '../../theme/system-utilities';
import { Flex } from '../Flex';
import { CarouselPagination } from '../image-carousel/CarouselPagination';
import { getSlideHref, useCarouselId } from '../image-carousel/utils';
import ResponsiveImage from '../ResponsiveImage';
import { WIDTHS } from './Image';

interface ImagesProps {
  images: GroupValueArticle['images'];
  fabricSample: Nullable<string>;
  name: string;
  url?: string;
}

const Container = styled.div<
  BoxShadowProps & TopProps & SpaceProps & BackgroundColorProps & PositionProps & ZIndexProps
>`
  ${backgroundColor};
  ${boxShadow};
  ${position};
  ${space};
  ${themeHeight};
  ${themeWidth};
  ${top};
  ${zIndex};
`;

const containerStyles: ComponentProps<typeof Container> = {
  p: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
  backgroundColor: 'sys.neutral.background.default',
  position: 'sticky',
  top: 0,
  zIndex: GO_TO_TOP_Z_INDEX,
  pb: 'sp_12',
};

const useGetImagesWithFabric = (
  images: ImagesProps['images'] = [],
  fabricSample: Nullable<string> = null
) => {
  const results = [...images];

  if (fabricSample) {
    // If a fabric sample is provided, insert it as the second item in the results array.
    results.splice(1, 0, { path: fabricSample, type: null });
  } else {
    // If no fabric sample is provided, check if any image in the array has the type 'FABRIC'.
    const fabricImageIndex = images.findIndex(image => image.type === ArticleImageType.Fabric);
    if (fabricImageIndex !== -1) {
      // If a fabric image exists, insert it as the second item in the results array.
      results.splice(1, 0, images[fabricImageIndex]);
    }
  }

  return results;
};

const PaginationWrapper = styled.div<
  ThemeBottomProps & LeftProps & OverflowProps & PositionProps & SpaceProps & TransformProps
>`
  ${left};
  ${overflow};
  ${position};
  ${space};
  ${themeBottom};
  ${transform};
`;

const paginationWrapperStyles: ComponentProps<typeof PaginationWrapper> = {
  left: '50%',
  overflow: 'hidden',
  position: 'absolute',
  transform: 'translateX(-50%)',
  pt: ['sp_4', 'sp_8'],
  themeBottom: '0px',
};

const getPaginationWrapper = p => (
  <PaginationWrapper {...paginationWrapperStyles} tabIndex={-1}>
    <CarouselPagination {...p} />
  </PaginationWrapper>
);

const GalleryWrapper = styled.div<DisplayProps>`
  ${display};
`;

const ImageGallery = ({ images, fabricSample, name, url }: ImagesProps) => {
  const imagesWithFabric = useGetImagesWithFabric(images, fabricSample);
  const [activeSlide, setActiveSlide] = React.useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveSlide(0);
  }, [images]);
  const baseId = useCarouselId();
  const props: ComponentProps<typeof ImageCarousel> = {
    baseId,
    widths: WIDTHS,
    name,
    items: imagesWithFabric,
    href: url,
    wrapAround: true,
    slidesToShow: 2,
    showNavigation: true,
    activeSlide,
    renderPagination: getPaginationWrapper,
    onAfterSlide: index => setActiveSlide(index),
  };

  const mobileProps: ComponentProps<typeof ImageCarousel> = {
    ...props,
    slidesToShow: 4 / 3,
  };

  if (imagesWithFabric.length === 0) {
    return null;
  }

  if (imagesWithFabric.length === 1) {
    return (
      <Container {...containerStyles} data-testid="product-mvc-images">
        <Flex
          justifyContent="center"
          {...(url ? { href: getSlideHref({ index: 0, href: url }), as: 'a' } : {})}
        >
          <ResponsiveImage src={imagesWithFabric[0].path} alt={name} widths={WIDTHS} />
        </Flex>
      </Container>
    );
  }

  return (
    <Container ref={containerRef} {...containerStyles} data-testid="product-mvc-images">
      <GalleryWrapper display={['block', 'none']} data-testid="carousel-wrapper-mobile">
        <ImageCarousel {...mobileProps} />
      </GalleryWrapper>
      <GalleryWrapper display={['none', 'block']} data-testid="carousel-wrapper">
        <ImageCarousel {...props} />
      </GalleryWrapper>
    </Container>
  );
};

export default ImageGallery;
